import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, History, Lock } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { Spinner } from "@/components/common";

const STATUS = {
  checking: ["Checking", "bg-sky-50 text-sky-700"], applied: ["Confirmed & applied", "bg-emerald-50 text-emerald-700"],
  rejected: ["Not supported", "bg-rose-50 text-rose-700"], unclear: ["Couldn't confirm", "bg-amber-50 text-amber-800"], reverted: ["Reverted by an editor", "bg-slate-100 text-slate-600"],
};

export const FactsStreak = () => {
  const { me } = useMe();
  const s = me?.contributions?.streak;
  if (!s) return null;
  const msg = s.weeks === 0 ? "Get a Fact verified, a correction confirmed, or confirm a Fact that sources later back this week to start a streak."
    : s.this_week ? "You've already contributed this week. Keep it going next week." : "Get a Fact verified, a correction confirmed, or confirm a Fact that sources later back this week to keep it going.";
  return (
    <div className="surface flex items-center gap-4 p-4" data-testid="facts-streak">
      <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${s.weeks ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-400"}`}><Flame size={22} aria-hidden /></span>
      <div className="min-w-0 flex-1">
        <p className="font-serif text-lg"><b data-testid="facts-streak-weeks">{s.weeks}</b> {s.weeks === 1 ? "week" : "weeks"} in a row</p>
        <p className="text-sm text-slate-500" data-testid="facts-streak-message">{msg}</p>
      </div>
      <div className="text-right text-xs text-slate-400"><p>Best</p><p className="text-base font-semibold text-slate-700" data-testid="facts-streak-best">{s.best}</p></div>
    </div>
  );
};

const CorrectionRow = ({ c }) => {
  const [label, cls] = STATUS[c.status] || [c.status, "bg-slate-100 text-slate-600"];
  return (
    <li className="space-y-1 border-t border-border py-3 text-sm first:border-0" data-testid={`my-correction-${c.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`} data-testid={`my-correction-status-${c.id}`}>{label}</span>
        {c.link ? <Link to={c.link} className="font-medium hover:underline" data-testid={`my-correction-link-${c.id}`}>{c.target_title}</Link> : <span className="font-medium">{c.target_title}</span>}
        <span className="ml-auto text-xs text-slate-400">{fmtDate(c.created_at)}</span>
      </div>
      <p className="text-slate-700">“{c.claim}”</p>
      {c.reason && <p className="text-xs text-slate-500">{c.reason}</p>}
      {c.changes?.map((ch, i) => <p key={i} className="text-xs text-slate-500"><s>{String(ch.old)}</s> → <b className="text-slate-800">{String(ch.new)}</b></p>)}
      {c.held && <p className="flex items-center gap-1 text-xs text-emerald-700" data-testid={`my-correction-held-${c.id}`}><Lock size={11} />Held until newer official data arrives</p>}
    </li>
  );
};

export const MyCorrections = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/corrections/mine").then((r) => setRows(r.data.items)).catch(() => setRows([])); }, []);
  return (
    <div className="surface p-4" data-testid="my-corrections">
      <h3 className="mb-1 flex items-center gap-2 font-serif text-lg"><History size={16} />Your confirmed corrections</h3>
      <p className="mb-2 text-xs text-slate-400">Corrections approved sources confirmed. Only you can see this list.</p>
      {rows === null ? <Spinner /> : !rows.length ? <p className="py-2 text-sm text-slate-500" data-testid="my-corrections-empty">No confirmed corrections yet. Use “Suggest a correction” on a Fact or a Live page.</p>
        : <ul>{rows.map((c) => <CorrectionRow key={c.id} c={c} />)}</ul>}
    </div>
  );
};

export const MyContributions = () => (
  <div className="mt-8 space-y-4" data-testid="my-contributions">
    <FactsStreak />
    <MyCorrections />
  </div>
);
